import { setProp } from '@shared/helpers/operators';
import StdForm from '@shared/components/forms/auto-form/std-form/std-form.vue';
import { Component, Prop } from 'vue-property-decorator';
import EntityTableProvider from '@shared/components/tables/entity-table-provider/entity-table-provider.vue';
import EntityTable from '@shared/components/tables/entity-table/entity-table.vue';
import BpcPageTitle from '@shared/layouts/components/page-title/page-title.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import EntityDeleteModalProvider from '@shared/components/modals/entity-delete-modal-provider/entity-delete-modal-provider.vue';
import ConfirmationModal from '@shared/components/modals/confirmation-modal/confirmation-modal.vue';
import { ValidationObserver } from 'vee-validate';
import { mixins } from 'vue-class-component';
import AbstractTable from '@shared/components/tables/abstract-table/abstract-table';
import StdFilter from '@shared/components/filters/std-filter/std-filter.vue';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faSync, faCalendarCheck, faDumbbell } from '@fortawesome/free-solid-svg-icons';
import { ActivityGroup } from '@modules/activities/interfaces/activity-group';
import BpcDrawerModal from '@shared/components/modals/drawer-modal/drawer-modal.vue';
import CustomFilterSelector from '@shared/components/filters/custom-filter-selector/custom-filter-selector.vue';
import ExtendedFilter from '@shared/components/filters/extended-filter/extended-filter.vue';
import BulkMoveToTrashModal from '@shared/components/modals/bulk-move-to-trash-modal/bulk-move-to-trash-modal.vue';
import BulkRecoverModal from '@shared/components/modals/bulk-recover-modal/bulk-recover-modal.vue';
import GenerateSessionsByTimeSlotsModal from '@activities/components/modals/generate-sessions-by-time-slots-modal/generate-sessions-by-time-slots-modal.vue';

library.add(faSync, faCalendarCheck, faDumbbell);

@Component({
  name: 'bpc-activity-group-table',
  components: {
    'bpc-entity-table-provider': EntityTableProvider,
    'bpc-entity-table': EntityTable,
    'bpc-page-title': BpcPageTitle,
    'font-awesome-icon': FontAwesomeIcon,
    'bpc-entity-delete-modal-provider': EntityDeleteModalProvider,
    'bpc-confirmation-modal': ConfirmationModal,
    'bpc-std-form': StdForm,
    'bpc-std-filter': StdFilter,
    ValidationObserver,
    'bpc-drawer-modal': BpcDrawerModal,
    'bpc-custom-filter-selector': CustomFilterSelector,
    'bpc-extended-filter': ExtendedFilter,
    'bpc-bulk-move-to-trash-modal': BulkMoveToTrashModal,
    'bpc-bulk-recover-modal': BulkRecoverModal,
    'bpc-generate-sessions-by-time-slots-modal': GenerateSessionsByTimeSlotsModal,
  },
})
export default class ActivityGroupTable extends mixins(AbstractTable) {
  @Prop(Boolean) showDrawerLarge: boolean;
  @Prop(String) declare readonly displayConfigName: string;
  @Prop(String) declare readonly title: string;
  @Prop(String) declare readonly subtitle: string;
  @Prop(Object) readonly externalFormModel: Record<string, any>;
  @Prop(Array) readonly initialFilters: Record<string, any>[];
  @Prop({
    type: Array,
    default() {
      return ['part1', 'part2'];
    },
  })
  declare readonly selectConfigName: string[];
  @Prop({ type: String, default: 'edit-activity-group' }) readonly editFormConfigName: string;
  @Prop({ type: Boolean, default: true }) readonly showTableHeader: boolean;
  @Prop({ type: Boolean, default: true }) readonly withFilters: boolean;
  @Prop({ type: Boolean, default: true }) readonly withSearch: boolean;
  @Prop({ type: Boolean, default: true }) readonly showOrderBy: boolean;
  @Prop({ type: Boolean, default: true }) readonly showPagination: boolean;
  @Prop({ type: Boolean, default: false }) readonly showTabFilters: boolean;
  @Prop({ type: Boolean, default: true }) readonly showExtendedFilters: boolean;

  public entity: ActivityGroup = null;
  public entityName = 'activity-group';
  public pluralEntityName = 'activity-groups';
  public showDrawerSettingSection: boolean = false;
  public showSettingForm: boolean = false;
  public permissionEntityNameToUse = 'activity_groups';
  public showGenerateSessionsActions: boolean = false;

  // State to manage generate session by time slot modal.
  public showGenerateSessionsModal = false;

  public flagsToCheckBeforeLoadEntities: string[] = [
    'configLoaded',
    'filtersLoaded',
    'paginationLoaded',
    'orderLoaded',
    'urlParametersLoaded',
  ];

  setEntityAttributesAfterSave() {
    const newEntity = {} as ActivityGroup;
    Object.keys(this.entity).forEach(key => {
      this.$set(newEntity, key, null);
    });

    this.$set(newEntity, 'status', this.entity.status);
    this.$set(this.$refs.stdForm, 'formModel', newEntity);

    this.showForm = true;
    this.showDrawerSection = true;
  }

  onNew() {
    this.$bpcStorage.clear('activity');
    this.$bpcStorage.clear('article');
    this.$bpcStorage.clear('contacts');
    this.showDrawerLarge = false;
    this.entity = null;
    this.showForm = true;
    this.showDrawerSection = true;
    this.disabledForm = false;
  }

  onEdit(e, disabled = false) {
    this.$bpcStorage.clear('activity');
    this.$bpcStorage.clear('article');
    this.$bpcStorage.clear('contacts');
    this.showDrawerLarge = true;
    this.entity = e.item;
    this.showForm = true;
    this.showDrawerSection = true;
    setTimeout(() => {
      this.disabledForm = disabled;
    });
  }

  // Bulk soft delete modal (move to trash)
  showBulkMoveToTrashModal = false;
  onBulkSoftDelete() {
    this.showBulkMoveToTrashModal = true;
  }
  onCLoseBulkSoftDelete() {
    this.showBulkMoveToTrashModal = false;
  }
  onSuccessSoftDelete() {
    this.$refs.softDeleteConfirmationModal.onCloseModal();
    this.$refs.provider.getEntities();
  }
  bulkMoveToTrashDataToShow = [['code', 'name']];

  // Bulk restore modal (recover)
  showBulkRecoverModal = false;

  onBulkRecover() {
    this.showBulkRecoverModal = true;
  }

  onCLoseBulkRecover() {
    this.showBulkRecoverModal = false;
  }

  onSuccessBulkRecover() {
    this.$refs.provider.getEntities();
  }

  onCloseShowGenerateSessionsModal() {
    this.showGenerateSessionsModal = false;
  }

  invoiceMarkAsPaidFinished() {
    this.$refs.provider.getEntities();
  }

  bulkRecoverDataToShow = [['code', 'name']];

  //LIFECYCLE
  // created() {}
  mounted() {
    this.$root.$on('activityOfGroupChanged', val => {
      const activityType = val?.activity_type;
      this.prepareGroupFormByActivityType(activityType);
      // Set activity period
      this.$refs.stdForm.formModel.start_date = val?.start_date;
      this.$refs.stdForm.formModel.end_date = val?.end_date;
    });
  }

  destroyed() {
    this.$root.$off('activityOfGroupChanged');
  }

  prepareGroupFormByActivityType(activityType) {
    this.showGenerateSessionsActions =
      this.$refs.stdForm.formModel.id &&
      (activityType === 'session' ||
        activityType === 'training' ||
        activityType === 'camp' ||
        activityType === 'course');

    setProp(this.$refs.stdForm.formModel, 'activity_type', activityType);
  }

  async openGenerateSessionModal() {
    this.nextAction = 'continue-editing';
    await this.$refs.stdForm.saveEntity();
    this.showGenerateSessionsModal = true;
  }

  onModelLoaded(event) {
    let activityType = this.$refs.stdForm.formModel.activity?.activity_type;
    if (!activityType) {
      activityType = this.externalFormModel?.activity_type;
    }

    this.prepareGroupFormByActivityType(activityType);
    this.$emit('model-loaded', event);
  }

  actionAfterPrepareToSave(entity: Partial<ActivityGroup>): Partial<ActivityGroup> {
    // We need to remove before sending because it has too many fields and the request doesn't receive all the data
    delete entity['activity'];
    return entity;
  }

  onGenerateSessionsFinished() {
    this.nextAction = null;
    if (this.isInput) {
      this.$root.$emit('activityOfGroupChangedFromTableInput');
    }
  }
}
